import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as RMouse } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useColumnDistinct, useColumnDistinctCounts } from '../modules/queries';
import type { ModuleResponse } from '../modules/types';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useFetchMs } from '../../hooks/useFetchMs';
import { fmtLocalDate } from '../../utils/datetime';
import { useNotify } from '../../components/notifications';
import { runLoading } from '../../components/loading';
import './trend.css';

const KEY = 'trendanalysis';
// token-exempt: chart — one colour per selected-parameter series (data-viz
// categorical palette; brand orange leads so a single series is unchanged).
const SERIES_PALETTE = ['#FB6E2B', '#2563eb', '#16a34a', '#a855f7', '#0ea5e9', '#dc2626', '#f59e0b', '#0d9488', '#7c3aed', '#db2777'];

type Row = Record<string, unknown>;
type ChartType = 'line' | 'bar' | 'area';
// How the chart splits into comparison lines (prototype v2 "Series (lines)").
type SeriesMode = 'single' | 'parameter' | 'method' | 'test';
interface Pt { date: number; value: number; raw: string; job: string; sample: string; site: string; client: string; pid: string; pname: string; units: string; rl: string; dateStr: string; series: string; param: string; test: string; method: string; }
interface Series { name: string; units: string; color: string; pts: Pt[]; }

const str = (v: unknown): string => (v == null ? '' : String(v));
// Shared §14b.6 helper (MM/DD/YYYY, no inline date code); raw fallback when
// the value isn't parseable.
const fmtDate = (v: unknown): string => {
  const s = str(v);
  if (!s) return '';
  return fmtLocalDate(s) || s;
};
const toNum = (v: unknown): number => {
  const n = parseFloat(str(v).replace(/[^0-9eE.+-]/g, ''));
  return Number.isFinite(n) ? n : NaN;
};
// Auto-fit the Y-axis to the data: pick the smallest "nice" ceiling just above
// the tallest bar (≈10% headroom), from the 1 · 1.5 · 2 · 2.5 · 3 · 4 · 5 · 7.5
// progression scaled to the value's magnitude. So when every result is small
// (e.g. all < 2), the axis lands on 0–2 / 0–3 instead of a fixed 0–5 and the
// bars look full-height (user request 2026-09-25).
const niceMax = (vMax: number): number => {
  if (!(vMax > 0)) return 1;
  const target = vMax * 1.1; // small headroom so the tallest bar isn't flush to the top
  const pow = Math.pow(10, Math.floor(Math.log10(target)));
  for (const m of [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10]) {
    const candidate = m * pow;
    if (candidate >= target) return candidate;
  }
  return 10 * pow;
};

/* ── Multi-select checked combo (v5 .tr-combo) ── */
function CheckedCombo({
  label, required, placeholder, column, selected, onChange, cascade,
}: {
  label: string; required?: boolean; placeholder: string; column: string;
  selected: string[]; onChange: (v: string[]) => void;
  cascade: Record<string, string[]>;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  // Options cascade on the OTHER combos' current selections. We pull the
  // per-value COUNT too (user request 2026-09-23 — show a count beside each
  // option); `options` is the plain value list the rest of the combo logic uses.
  const { data: withCounts = [], isLoading } = useColumnDistinctCounts(KEY, column, '', null, true, cascade);
  const options = useMemo(() => withCounts.map((o) => o.value), [withCounts]);
  const countOf = useMemo(() => {
    const m: Record<string, number> = {};
    for (const o of withCounts) m[o.value] = o.count;
    return m;
  }, [withCounts]);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  // Clear the in-combo search text whenever the dropdown closes, so Reset (or
  // simply reopening) always starts from a fresh, unfiltered list rather than
  // keeping the last typed filter (user report 2026-09-24).
  useEffect(() => { if (!open) setQ(''); }, [open]);

  const shown = useMemo(
    () => options.filter((o) => o.toLowerCase().includes(q.trim().toLowerCase())),
    [options, q],
  );
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const display = selected.length === 0 ? placeholder
    : selected.length === 1 ? selected[0]! : `${selected.length} selected`;

  return (
    <div className="tr-field">
      <div className="tr-field-lbl">{label}{required && <span className="req" title="Required">*</span>}</div>
      <div className={`tr-combo${open ? ' open' : ''}${selected.length ? ' has-value' : ''}`} ref={ref}>
        <div className="tr-combo-display" onClick={() => setOpen((o) => !o)}>
          <span className={`tr-combo-value${selected.length ? '' : ' placeholder'}`}>{display}</span>
          {selected.length > 0 && (
            <span className="tr-combo-clear" onClick={(e) => { e.stopPropagation(); onChange([]); }}>&times;</span>
          )}
          <i className="ti ti-chevron-down chev" />
        </div>
        {open && (
          <div className="tr-combo-menu">
            <div className="tr-combo-search">
              <input autoFocus placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="tr-combo-actions">
              <a onClick={() => onChange(shown)}>Select all</a>
              <a onClick={() => onChange([])}>Clear</a>
              <span className="tr-combo-count">{selected.length} / {options.length}</span>
            </div>
            <div className="tr-combo-options">
              {isLoading && <div className="tr-combo-empty">Loading…</div>}
              {!isLoading && shown.length === 0 && <div className="tr-combo-empty">No matches</div>}
              {shown.map((o) => (
                <label key={o}>
                  <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} />
                  <span className="tr-combo-opt-lbl">{o || '(blank)'}</span>
                  <span className="tr-combo-opt-count">{(countOf[o] ?? 0).toLocaleString()}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* Method is now a <CheckedCombo> dropdown like Test (user request 2026-09-23) —
   the inline MethodPicker checkbox box was removed for a consistent filter row. */

export function TrendAnalysisPage({ onHome }: { onHome: () => void }) {
  const [client, setClient] = useState<string[]>([]);
  const [projectId, setProjectId] = useState<string[]>([]);
  const [projectName, setProjectName] = useState<string[]>([]);
  const [siteName, setSiteName] = useState<string[]>([]);
  const [parameter, setParameter] = useState<string[]>([]);
  // Test = multi-select checkbox combo (user request 2026-09-23 — same as the
  // other combos); Method = multi-select checkbox list. Both cascade off the
  // panel and further narrow the results + chart.
  const [test, setTest] = useState<string[]>([]);
  const [method, setMethod] = useState<string[]>([]);
  // Live selections drive each combo's cascade (useColumnDistinct drops the
  // combo's own column so its list isn't restricted by itself). Test travels as
  // a 0/1-length array so it plugs into the same cascade + filter shape.
  const cascade = { client, projectId, projectName, siteName, parameter, test, method };
  // Method options + a STABLE colour per method — shared by the inline Method
  // picker's swatches AND the chart's by-Method series, so a method's dot always
  // matches its line. The distinct hook drops 'method', so the list (and thus the
  // colours) don't change as you tick methods.
  const methodOptionsQ = useColumnDistinct(KEY, 'method', '', null, true, cascade);
  const methodColors = useMemo(() => {
    const m: Record<string, string> = {};
    (methodOptionsQ.data ?? []).forEach((name, i) => { m[name] = SERIES_PALETTE[i % SERIES_PALETTE.length]!; });
    return m;
  }, [methodOptionsQ.data]);
  // When "Series = Multiple — by Method" is chosen, auto-CHECK every available
  // method so the picker shows them ticked (matching the plotted lines). Handled
  // imperatively in the Series dropdown's onChange (no effect → no update loop).
  const pickSeriesMode = (mode: SeriesMode) => {
    setSeriesMode(mode);
    if (mode === 'method' && method.length === 0) {
      const opts = methodOptionsQ.data ?? [];
      if (opts.length) setMethod(opts);
    }
  };
  // Date-range filter (user request 2026-09-23) — a 1M/3M/6M/1Y/All chip that
  // drives Date Analyzed From/To. Defaults to 1Y (365 days), so the two date
  // fields open pre-filled to the last year.
  const ymd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [rangeDays, setRangeDays] = useState<number | null>(365);
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 365); return ymd(d); });
  const [dateTo, setDateTo] = useState(() => ymd(new Date()));
  const applyRange = (days: number | null) => {
    setRangeDays(days);
    if (days == null) { setDateFrom(''); setDateTo(''); return; }
    const from = new Date(); from.setDate(from.getDate() - days);
    setDateFrom(ymd(from)); setDateTo(ymd(new Date()));
  };
  const [chartType, setChartType] = useState<ChartType>('line');
  // Series (lines): how the chart splits into comparison series (prototype v2).
  const [seriesMode, setSeriesMode] = useState<SeriesMode>('parameter');
  const [committed, setCommitted] = useState<null | {
    client: string[]; projectId: string[]; projectName: string[];
    siteName: string[]; parameter: string[]; test: string[]; method: string[];
    from: string; to: string;
  }>(null);

  const [gSearch, setGSearch] = useState('');
  const tipRef = useRef<HTMLDivElement>(null);
  const notify = useNotify();

  // "Export Chart ▾" dropdown (Export PDF = chart + grid; Export Excel = grid).
  // Portalled to <body> with FIXED positioning + click-outside on BOTH the
  // trigger and the menu — the same robust pattern the shared <ExportMenu> uses,
  // so an ancestor's `overflow`/stacking context can never clip or swallow it
  // (the earlier absolute-positioned menu "did nothing" on click, 2026-09-21).
  const [expOpen, setExpOpen] = useState(false);
  const expWrapRef = useRef<HTMLDivElement>(null);
  const expBtnRef = useRef<HTMLButtonElement>(null);
  const expMenuRef = useRef<HTMLDivElement>(null);
  const [expPos, setExpPos] = useState<{ top: number; left: number } | null>(null);

  // Position the menu directly under the trigger (viewport-clamped). Computed
  // SYNCHRONOUSLY on click and on scroll/resize, so the menu is already placed on
  // its very first paint — no visibility-hidden flash, no "nothing happens" race.
  const placeExp = useCallback(() => {
    const b = expBtnRef.current?.getBoundingClientRect();
    if (!b) return;
    const GAP = 4, EDGE = 8, mw = 220;
    const vw = window.innerWidth;
    const top = b.bottom + GAP;
    const left = Math.min(Math.max(EDGE, b.right - mw), Math.max(EDGE, vw - mw - EDGE));
    setExpPos({ top, left });
  }, []);

  const toggleExport = useCallback(() => {
    setExpOpen((o) => {
      if (!o) placeExp();          // compute position up front, before the menu paints
      return !o;
    });
  }, [placeExp]);

  useEffect(() => {
    if (!expOpen) return;
    const reflow = () => placeExp();
    window.addEventListener('scroll', reflow, true);
    window.addEventListener('resize', reflow);
    return () => { window.removeEventListener('scroll', reflow, true); window.removeEventListener('resize', reflow); };
  }, [expOpen, placeExp]);

  useEffect(() => {
    if (!expOpen) return;
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (expWrapRef.current?.contains(t) || expMenuRef.current?.contains(t)) return;
      setExpOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [expOpen]);

  const dataQ = useQuery({
    queryKey: ['trend-data', committed],
    enabled: committed != null,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    queryFn: async () => {
      const f: Record<string, string[]> = {};
      if (committed!.client.length) f.client = committed!.client;
      if (committed!.projectId.length) f.projectId = committed!.projectId;
      if (committed!.projectName.length) f.projectName = committed!.projectName;
      if (committed!.siteName.length) f.siteName = committed!.siteName;
      if (committed!.parameter.length) f.parameter = committed!.parameter;
      // Test + Method are NOT sent to the server — they filter the loaded rows
      // client-side (live) so picking a Test/Method updates the chart + grid
      // instantly without another Run Query.
      const params: Record<string, string | number> = { page: 1, pageSize: 10000 };
      if (Object.keys(f).length) params.filters = JSON.stringify(f);
      const { data } = await api.get<ModuleResponse>(`/api/v1/modules/${KEY}`, { params });
      return data;
    },
  });
  // Top load-time chip (shared) — times each fetch via the query's isFetching.
  const loadMs = useFetchMs(dataQ.isFetching);

  // Rows after client-side date + LIVE Test/Method filtering. Test and Method
  // are applied here (not on the server) so changing them updates the chart +
  // grid instantly, without another Run Query.
  const rows = useMemo(() => {
    const all = (dataQ.data?.data ?? []) as Row[];
    if (!committed) return [];
    const from = committed.from ? new Date(committed.from).getTime() : null;
    const to = committed.to ? new Date(committed.to).getTime() + 86_400_000 : null;
    return all.filter((r) => {
      const t = new Date(str(r.dateAnalyzed)).getTime();
      if (Number.isNaN(t)) return false;
      if (from != null && t < from) return false;
      if (to != null && t >= to) return false;
      if (test.length && !test.includes(str(r.test))) return false;
      if (method.length && !method.includes(str(r.method))) return false;
      return true;
    });
  }, [dataQ.data, committed, test, method]);

  // Comparison series, split by the "Series (lines)" mode (prototype v2):
  //   single    → ONE line (all numeric results together)
  //   parameter → one line per Parameter
  //   method    → one line per Method (the prototype's method comparison)
  //   test      → one line per Test
  // A result with no numeric value (e.g. E. coli = Absent/Present) can't be
  // plotted, so it's dropped from the chart (still shown in the grid). Each
  // series gets its own colour + legend entry.
  const series = useMemo<Series[]>(() => {
    const raw: Pt[] = [];
    for (const r of rows) {
      const v = toNum(r.result);
      const t = new Date(str(r.dateAnalyzed)).getTime();
      if (Number.isNaN(v) || Number.isNaN(t)) continue;
      raw.push({
        date: t, value: v, raw: str(r.result), job: str(r.jobId), sample: str(r.sampleId),
        site: str(r.siteName), client: str(r.client), pid: str(r.projectId),
        pname: str(r.projectName), units: str(r.units), rl: str(r.rl),
        dateStr: fmtDate(r.dateAnalyzed), series: '',
        param: str(r.parameter), test: str(r.test), method: str(r.method),
      });
    }
    if (raw.length === 0) return [];
    const singleName = committed?.parameter.length === 1 ? committed.parameter[0]! : 'Results';
    const keyOf = (p: Pt) =>
      seriesMode === 'single' ? singleName
      : seriesMode === 'method' ? (p.method || '(no method)')
      : seriesMode === 'test' ? (p.test || '(no test)')
      : (p.param || '(no parameter)');
    const groups = new Map<string, Pt[]>();
    const order: string[] = [];
    for (const p of raw) {
      const k = keyOf(p);
      p.series = k;
      if (!groups.has(k)) { groups.set(k, []); order.push(k); }
      groups.get(k)!.push(p);
    }
    return order.map((k, i) => {
      const gp = groups.get(k)!;
      gp.sort((a, b) => a.date - b.date);
      // By-Method series take the shared method colour so the line matches the
      // Method picker's swatch; other modes cycle the palette by order.
      const color = seriesMode === 'method'
        ? (methodColors[k] ?? SERIES_PALETTE[i % SERIES_PALETTE.length]!)
        : SERIES_PALETTE[i % SERIES_PALETTE.length]!;
      return { name: k, units: gp[0]?.units ?? '', color, pts: gp };
    });
  }, [committed, rows, seriesMode, methodColors]);

  // All numeric points across every series (drives count + shared axes).
  const points = useMemo<Pt[]>(() => series.flatMap((s) => s.pts), [series]);
  // Y-axis unit label — the common unit when every series shares one, else blank.
  const units = useMemo(() => {
    const us = Array.from(new Set(series.map((s) => s.units).filter(Boolean)));
    return us.length === 1 ? us[0]! : '';
  }, [series]);

  // ── Chart geometry — shared time + value axes across ALL series ──
  const chart = useMemo(() => {
    // padL/padB leave room for the axis TITLES (Result (unit) / Date Analyzed) —
    // user request 2026-09-24 (professional, labelled axes like the reference).
    const W = 1000, H = 320, padL = 58, padR = 20, padT = 20, padB = 62;
    const plotW = W - padL - padR, plotH = H - padT - padB;
    if (points.length === 0) return null;
    const tMin = Math.min(...points.map((p) => p.date));
    const tMax = Math.max(...points.map((p) => p.date));
    const tSpan = Math.max(tMax - tMin, 1);
    // Outlier-robust Y axis (user request 2026-09-24): with many parameters of
    // wildly different units, ONE extreme value (e.g. a 1.5M count next to µg/L
    // traces) flattens every other bar to nothing. When the true max is a lone
    // outlier (> 3× the 95th percentile), scale the axis to the 95th percentile
    // instead so the bulk of the data is readable; such extreme values then CLIP
    // at the top (their exact figure stays in the tooltip + the grid). Normal
    // data (max ≈ P95) is unaffected.
    const sortedV = points.map((p) => p.value).sort((a, b) => a - b);
    const vMax = sortedV[sortedV.length - 1] ?? 0;
    const pct = (qv: number) => sortedV[Math.min(sortedV.length - 1, Math.max(0, Math.floor(qv * (sortedV.length - 1))))] ?? 0;
    const p95 = pct(0.95);
    const axisMax = (p95 > 0 && vMax > p95 * 3) ? p95 : vMax;
    const yMax = niceMax(axisMax);
    // Horizontal inset so the FIRST/LAST points don't sit exactly on the axis
    // edges — otherwise a bar (centred on its point) or an edge dot spills past
    // the plot area (user report 2026-09-24: "bar/line/area must not cross the
    // axis"). The inset reserves room on both sides; bar x + dot cx are also
    // hard-clamped to the plot bounds below as a final guard.
    const xInset = Math.min(46, plotW * 0.06);
    const innerW = Math.max(1, plotW - xInset * 2);
    const xMinPx = padL, xMaxPx = W - padR;
    const xOf = (t: number) => padL + xInset + ((t - tMin) / tSpan) * innerW;
    // Clamp to yMax so a clipped outlier sits ON the top edge, never above it.
    const yOf = (v: number) => padT + plotH - (Math.min(v, yMax) / yMax) * plotH;
    // Per-series geometry (xs/ys + line/area paths).
    const built = series.map((s) => {
      const xs = s.pts.map((p) => xOf(p.date));
      const ys = s.pts.map((p) => yOf(p.value));
      const linePath = xs.map((x, i) => `${i ? 'L' : 'M'} ${x.toFixed(1)},${ys[i]!.toFixed(1)}`).join(' ');
      const areaPath = xs.length
        ? `M ${xs[0]!.toFixed(1)},${padT + plotH} ${xs.map((x, i) => `L ${x.toFixed(1)},${ys[i]!.toFixed(1)}`).join(' ')} L ${xs[xs.length - 1]!.toFixed(1)},${padT + plotH} Z`
        : '';
      return { ...s, xs, ys, linePath, areaPath };
    });
    const yTicks = Array.from({ length: 6 }, (_, i) => {
      const y = padT + plotH - (i / 5) * plotH;
      const val = (i / 5) * yMax;
      return { y, label: yMax >= 10 ? Math.round(val).toString() : val.toFixed(1) };
    });
    // up to ~12 evenly spaced date ticks
    // chart-axis label — permitted §18.6 exception (M/YY tick, 24-hour-safe,
    // never AM/PM; not a data value)
    const nT = Math.min(12, Math.max(2, points.length));
    const xTicks = Array.from({ length: nT }, (_, i) => {
      const t = tMin + (i / (nT - 1)) * tSpan;
      const d = new Date(t);
      // Align ticks with the inset data range so labels sit under their points.
      return { x: padL + xInset + (i / (nT - 1)) * innerW, label: `${d.getMonth() + 1}/${d.getFullYear() % 100}` };
    });
    // ── Bar geometry — GROUPED (clustered) bars so multiple series sit side by
    //    side at each date instead of overlapping. Slot width comes from the
    //    tightest gap between adjacent points (clamped), the group fills ~72% of
    //    the slot, and each series gets an equal share within the group. ──
    const seriesCount = Math.max(1, built.length);
    const allXs = points.map((p) => xOf(p.date)).sort((a, b) => a - b);
    let minGap = plotW;
    for (let i = 1; i < allXs.length; i++) {
      const g = allXs[i]! - allXs[i - 1]!;
      if (g > 0.5 && g < minGap) minGap = g;
    }
    if (!Number.isFinite(minGap) || allXs.length < 2) minGap = plotW / 3;
    const slot = Math.max(10, Math.min(minGap, 72));
    const groupW = slot * 0.72;
    const barW = Math.max(4, groupW / seriesCount);
    const barRx = Math.min(4, barW / 2);
    const baseY = padT + plotH;
    return { W, H, padL, padR, padT, padB, plotH, built, yTicks, xTicks,
             seriesCount, groupW, barW, barRx, baseY, xMinPx, xMaxPx };
  }, [series, points]);

  const showTip = (e: RMouse, p: Pt) => {
    const tip = tipRef.current; if (!tip) return;
    // Minimal HTML-escape — values are lab data rendered via innerHTML.
    const esc = (v: string) => v.replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'));
    // A labelled "Label: value" row (label muted, value in the ink colour).
    const kv = (label: string, value: string) =>
      value ? `<div class="tr-tip-kv"><span class="tr-tip-k">${label}:</span> <span class="tr-tip-v">${esc(value)}</span></div>` : '';
    const resultText = `${p.raw}${p.units ? ` ${p.units}` : ''}`.trim();
    tip.innerHTML =
      kv('Result', resultText) +
      kv('Parameter', p.param) +
      kv('Test', p.test) +
      kv('Method', p.method) +
      kv('Date', p.dateStr) +
      kv('Site', p.site) +
      kv('Job', p.job);
    tip.classList.add('on');
    moveTip(e);
  };
  const moveTip = (e: RMouse) => {
    const tip = tipRef.current; if (!tip || !tip.classList.contains('on')) return;
    tip.style.left = `${Math.min(e.clientX + 14, window.innerWidth - tip.offsetWidth - 10)}px`;
    tip.style.top = `${Math.max(e.clientY - 40, 4)}px`;
  };
  const hideTip = () => tipRef.current?.classList.remove('on');

  // Cascading reset — changing (or clearing) a parent wipes every dependent
  // child below it so they reload to their fresh state, AND clears the committed
  // query so the chart + grid reset until the next Run Query (user request
  // 2026-09-21). Order: Client → Project Code → Project Name → Site Name →
  // Parameter → Test → Method.
  const onClient = (v: string[]) => { setClient(v); setProjectId([]); setProjectName([]); setSiteName([]); setParameter([]); setTest([]); setMethod([]); setCommitted(null); };
  const onProjectId = (v: string[]) => { setProjectId(v); setProjectName([]); setSiteName([]); setParameter([]); setTest([]); setMethod([]); setCommitted(null); };
  const onProjectName = (v: string[]) => { setProjectName(v); setSiteName([]); setParameter([]); setTest([]); setMethod([]); setCommitted(null); };
  const onSiteName = (v: string[]) => { setSiteName(v); setParameter([]); setTest([]); setMethod([]); setCommitted(null); };
  // Parameter is a server-side filter, so changing it clears the committed query
  // (a re-run is needed) — but it must NOT wipe Test/Method, which are LIVE
  // client-side filters the user may have already chosen (user report 2026-09-23:
  // picking a Parameter was unchecking Test + Method).
  const onParameter = (v: string[]) => { setParameter(v); setCommitted(null); };
  // Test + Method are LIVE client-side filters — they narrow the already-loaded
  // chart + grid instantly and DON'T reset the committed query (no re-run needed).
  // Changing the Test clears the Method selection so it can't over-restrict.
  const onTest = (v: string[]) => { setTest(v); setMethod([]); };
  const onMethod = (v: string[]) => setMethod(v);

  const runQuery = () => {
    // Site is optional here: in this ALPACA DB only ~47 of ~30k result
    // rows carry a site, so requiring it (like ALPACA OS) would return
    // almost nothing. Leave blank = all sites.
    setCommitted({ client, projectId, projectName, siteName, parameter, test, method, from: dateFrom, to: dateTo });
  };
  const resetQuery = () => {
    setClient([]); setProjectId([]); setProjectName([]); setSiteName([]); setParameter([]);
    setTest([]); setMethod([]);
    // Reset restores the default 1Y window (not empty) so the date fields never
    // fall back to the native "mm/dd/yyyy" placeholder (user request 2026-09-23).
    applyRange(365);
    setChartType('line'); setCommitted(null);
  };

  // Grid rows after the page-level search box (AppDataGrid owns sort,
  // per-column filter and pagination).
  const gridRows = useMemo(() => {
    const q = gSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.jobId, r.sampleId, r.siteName, r.parameter, r.client, r.projectId, r.projectName]
        .some((v) => str(v).toLowerCase().includes(q)));
  }, [rows, gSearch]);

  // Shared-grid columns — Date uses the existing fmtDate; JobID + Result keep
  // their bold emphasis via a custom renderer (text mirrors it for filter/sort).
  const gridCols: AppColumn<Row>[] = [
    { field: 'dateAnalyzed', header: 'Date Analyzed', width: 110,
      render: r => fmtDate(r.dateAnalyzed), text: r => fmtDate(r.dateAnalyzed) },
    { field: 'jobId',       header: 'JobID',       width: 110,
      render: r => <strong>{str(r.jobId)}</strong>, text: r => str(r.jobId) },
    { field: 'sampleId',    header: 'SampleID',    width: 120, text: r => str(r.sampleId) },
    { field: 'siteName',    header: 'SiteName',    width: 170, text: r => str(r.siteName) },
    { field: 'parameter',   header: 'Parameter',   width: 150, text: r => str(r.parameter) },
    // Test + Method — after Parameter (from Testparameter → TestMethod → Method).
    { field: 'test',        header: 'Test',        width: 130, text: r => str(r.test) },
    { field: 'method',      header: 'Method',      width: 140, text: r => str(r.method) },
    { field: 'result',      header: 'Result',      width: 90,
      render: r => <strong>{str(r.result)}</strong>, text: r => str(r.result) },
    { field: 'units',       header: 'Units',       width: 70,  text: r => str(r.units) },
    { field: 'rl',          header: 'RL',          width: 65,  text: r => str(r.rl) },
    { field: 'client',      header: 'Client',      width: 180, text: r => str(r.client) },
    { field: 'projectId',   header: 'ProjectID',   width: 130, text: r => str(r.projectId) },
    { field: 'projectName', header: 'ProjectName',              text: r => str(r.projectName) },
  ];

  const doExport = async (kind: 'pdf' | 'xlsx') => {
    setExpOpen(false);
    if (committed == null || gridRows.length === 0) {
      notify.warning('Run a query first — there is nothing to export.');
      return;
    }
    try {
      await runLoading(exportNow(kind), { blocking: true, label: `Exporting ${kind.toUpperCase()}…` });
      notify.success(`Exported to ${kind.toUpperCase()} successfully.`);
    } catch (e) {
      notify.error(e instanceof Error ? `Export failed: ${e.message}` : 'Export failed.');
    }
  };

  const exportNow = async (kind: 'pdf' | 'xlsx') => {
    // Title = the single parameter's name, else a generic multi-parameter label.
    const chartName = series.length === 1 ? series[0]!.name : 'Trend';
    const base = `${(series.length === 1 ? series[0]!.name : 'trend').replace(/[^\w]+/g, '_')}_trend_${new Date().toISOString().slice(0, 10)}`;
    if (kind === 'xlsx') {
      const XLSX = await import('xlsx');
      const aoa = [
        ['Date Analyzed', 'JobID', 'SampleID', 'SiteName', 'Parameter', 'Test', 'Method', 'Result', 'Units', 'RL', 'Client', 'ProjectID', 'ProjectName'],
        ...gridRows.map((r) => [
          fmtDate(r.dateAnalyzed), str(r.jobId), str(r.sampleId), str(r.siteName), str(r.parameter),
          str(r.test), str(r.method),
          str(r.result), str(r.units), str(r.rl), str(r.client), str(r.projectId), str(r.projectName),
        ]),
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), 'Trend');
      XLSX.writeFile(wb, `${base}.xlsx`);
    } else {
      const svg = document.getElementById('tr-chart-svg') as SVGSVGElement | null;
      const { jsPDF } = await import('jspdf');
      await import('jspdf-autotable');
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const midX = pageW / 2;

      // Title — black, CENTERED (user request 2026-09-21).
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(13);
      doc.text(`${chartName} — Trend Analysis (${points.length} results)`, midX, 38, { align: 'center' });

      // Selected filter criteria (the values actually used for the shown data) —
      // BLACK, small, CENTERED under the title (user request 2026-09-21: the chosen
      // Client etc. must appear in the PDF, black, smaller font, centered).
      const cr = committed;
      const parts: string[] = [];
      const add = (label: string, arr?: string[]) => { if (arr && arr.length) parts.push(`${label}: ${arr.join(', ')}`); };
      add('Client', cr?.client);
      add('Project Code', cr?.projectId);
      add('Project Name', cr?.projectName);
      add('Site Name', cr?.siteName);
      add('Parameter', cr?.parameter);
      if (cr?.test?.length) parts.push(`Test: ${cr.test.join(', ')}`);
      add('Method', cr?.method);
      if (cr?.from || cr?.to) parts.push(`Date: ${cr?.from || '…'} – ${cr?.to || '…'}`);
      let chartY = 54;
      if (parts.length) {
        doc.setFontSize(9);
        doc.setTextColor(60, 60, 60);
        const critLines = doc.splitTextToSize(parts.join('   |   '), pageW - 80) as string[];
        doc.text(critLines, midX, 54, { align: 'center' });
        chartY = 54 + critLines.length * 11 + 8;
        doc.setTextColor(0, 0, 0);
      }

      // Page 1 — the chart image, below the criteria.
      if (svg) {
        const xml = new XMLSerializer().serializeToString(svg);
        const img = new Image();
        const url = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(xml)));
        await new Promise<void>((res) => { img.onload = () => res(); img.onerror = () => res(); img.src = url; });
        const canvas = document.createElement('canvas');
        canvas.width = 1000; canvas.height = 320;
        const ctx = canvas.getContext('2d');
        if (ctx) { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 1000, 320); ctx.drawImage(img, 0, 0, 1000, 320); }
        doc.addImage(canvas.toDataURL('image/png'), 'PNG', 40, chartY, 760, 243);
      }
      // Then the RESULTS grid (all filtered rows) as a table on the next page(s).
      const head = [['Date Analyzed', 'JobID', 'SampleID', 'SiteName', 'Parameter', 'Test', 'Method', 'Result', 'Units', 'RL', 'Client', 'ProjectID', 'ProjectName']];
      const body = gridRows.map((r) => [
        fmtDate(r.dateAnalyzed), str(r.jobId), str(r.sampleId), str(r.siteName), str(r.parameter),
        str(r.test), str(r.method), str(r.result), str(r.units), str(r.rl), str(r.client), str(r.projectId), str(r.projectName),
      ]);
      (doc as unknown as { autoTable: (o: unknown) => void }).autoTable({
        head, body, startY: svg ? chartY + 243 + 10 : chartY,
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [224, 123, 57] },   // token-exempt: PDF header (brand orange)
        margin: { top: 40, left: 20, right: 20 },
        // (Removed the per-page "Results (N rows)" header — it overlapped the
        // centered title on the chart page. User request 2026-09-21.)
      });
      doc.save(`${base}.pdf`);
    }
  };

  const c = chart;

  // Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6, ADR-0013): the query
  // panel's primary actions — Run Query (alt+r; on this read-only page Run REPLACES
  // Refresh, per the toolbar's own note) and Export Chart (alt+x; opens the
  // PDF / Excel menu).  Both live in the <AppToolbar> left/right slots, so the
  // toolbar does not auto-bind them.  Registered unconditionally, before the return.
  useHotkeys([
    { combo: 'alt+r', label: 'Run Query', group: 'Trend Analysis', order: 1, run: () => runQuery() },
    { combo: 'alt+x', label: 'Export Chart', group: 'Trend Analysis', order: 2,
      run: () => toggleExport() },
  ]);

  return (
    <div className="trend-page">
      <div className="tp-topbar">
        <div className="tp-breadcrumb">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>QA/QC</span><i className="ti ti-chevron-right" />Trend Analysis
        </div>
        {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour via
            navMeta('trendanalysis') + the SINGLE TOP load-time chip. */}
        <FormTitle pageId="trendanalysis" loadMs={loadMs} loading={committed != null && dataQ.isFetching} />
        <div className="tp-sub mlv-subcap">
          Read-only · Time-series analytics across ALPACA LIMS
        </div>
        {/* Date-range filter (user request 2026-09-23) — drives Date Analyzed
            From/To; defaults to 1Y. */}
        <div style={{ marginTop: 2 }}>
          <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
            value={rangeDays} onChange={applyRange} />
        </div>
      </div>

      <div className="tp-scroll">
        {/* Query Panel */}
        <div className="tp-card">
          <div className="tp-card-hdr">
            <div className="tp-card-title"><i className="ti ti-filter" /> Query Panel</div>
            {/* Shared toolbar — read-only analytics (no CRUD).  Run replaces
                Refresh and stays in the left slot alongside Reset; the chart
                export (PDF / XLSX) is the canonical "Export to", keeping its
                dropdown anchored via the right slot. */}
            <AppToolbar
              left={
                <>
                  <button className="ca-btn ca-btn-solid" type="button" onClick={runQuery}
                    title={`Run Query (${formatCombo('alt+r')})`}><i className="ti ti-search" /> Run Query</button>
                  <button className="ca-btn" type="button" onClick={resetQuery}><i className="ti ti-x" /> Reset</button>
                </>
              }
              right={
                <div className="export-menu-wrap" ref={expWrapRef}>
                  <button ref={expBtnRef} className="ca-btn" type="button"
                    onClick={toggleExport}
                    title={`Export Chart (${formatCombo('alt+x')})`}>
                    <i className="ti ti-download" /> Export Chart <i className="ti ti-chevron-down" style={{ fontSize: 11, opacity: .6 }} />
                  </button>
                  {expOpen && createPortal(
                    <div ref={expMenuRef} className="export-menu export-menu-fixed"
                      style={{ top: expPos?.top ?? 0, left: expPos?.left ?? 0 }}>
                      <div className="export-menu-hdr">Export chart</div>
                      <div className="export-menu-item" onClick={() => doExport('pdf')}><i className="ti ti-file-type-pdf" /><span>Export PDF (chart + grid)</span></div>
                      <div className="export-menu-item" onClick={() => doExport('xlsx')}><i className="ti ti-file-spreadsheet" /><span>Export Excel (.xlsx)</span></div>
                    </div>,
                    document.body,
                  )}
                </div>
              }
            />
          </div>
          {/* Field order (user request 2026-09-23): Client · ProjectID · Project
              Name · Site Name · Test · Method  /  Parameter · Date Analyzed From ·
              Date Analyzed To · Chart Type · Series (lines). ("Project Code" was
              renamed to "ProjectID"; Client re-added on request.) */}
          <div className="tr-query-grid">
            <CheckedCombo label="Client" placeholder="(All)" column="client" selected={client} onChange={onClient} cascade={cascade} />
            <CheckedCombo label="ProjectID" placeholder="(All)" column="projectId" selected={projectId} onChange={onProjectId} cascade={cascade} />
            <CheckedCombo label="Project Name" placeholder="(All)" column="projectName" selected={projectName} onChange={onProjectName} cascade={cascade} />
            <CheckedCombo label="Site Name" placeholder="(All)" column="siteName" selected={siteName} onChange={onSiteName} cascade={cascade} />
            <CheckedCombo label="Test" placeholder="(All)" column="test" selected={test} onChange={onTest} cascade={cascade} />
            <CheckedCombo label="Method" placeholder="(All)" column="method" selected={method} onChange={onMethod} cascade={cascade} />
          </div>
          <div className="tr-query-grid row2">
            <CheckedCombo label="Parameter" placeholder="(All)" column="parameter" selected={parameter} onChange={onParameter} cascade={cascade} />
            <div className="tr-field">
              <div className="tr-field-lbl">Date Analyzed From</div>
              <input type="date" className="tr-field-sel" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="tr-field">
              <div className="tr-field-lbl">Date Analyzed To</div>
              <input type="date" className="tr-field-sel" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div className="tr-field">
              <div className="tr-field-lbl">Chart Type</div>
              <select className="tr-field-sel" value={chartType} onChange={(e) => setChartType(e.target.value as ChartType)}>
                <option value="line">Line</option>
                <option value="bar">Bar</option>
                <option value="area">Area</option>
              </select>
            </div>
            {/* Series (lines) — split the chart into comparison series (prototype v2).
                Live: regroups the existing results without a re-query. */}
            <div className="tr-field">
              <div className="tr-field-lbl">Series (lines)</div>
              <select className="tr-field-sel" value={seriesMode} onChange={(e) => pickSeriesMode(e.target.value as SeriesMode)}>
                <option value="single">Single line</option>
                <option value="parameter">Multiple — by Parameter</option>
                <option value="method">Multiple — by Method</option>
                <option value="test">Multiple — by Test</option>
              </select>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="tp-card">
          <div className="tp-card-hdr">
            <div className="tp-card-title">
              <i className="ti ti-chart-line" />{' '}
              {series.length <= 1
                ? <><span>{series[0]?.name || '—'}</span> trend</>
                : <><span>{series.length} {seriesMode === 'method' ? 'methods' : seriesMode === 'test' ? 'tests' : 'parameters'}</span> compared</>}
              <span className="count">({points.length.toLocaleString()} results{series.length > 1 ? ` · ${series.length} series` : ''})</span>
            </div>
          </div>
          <div className="tr-chart-wrap">
            {/* Legend — one swatch per plotted parameter series. */}
            <div className="tr-chart-legend">
              {series.length === 0
                ? <><span className="swatch" /> —</>
                : series.map((s) => (
                    <span key={s.name} className="tr-legend-item">
                      <span className="swatch" style={{ background: s.color }} />
                      {s.name}{s.units && ` (${s.units})`}
                    </span>
                  ))}
            </div>
            {committed == null ? (
              <div className="tr-chart-empty">Set filters and click <b>&nbsp;Run Query</b></div>
            ) : dataQ.isFetching ? (
              <div className="tr-chart-empty">Loading…</div>
            ) : !c ? (
              <div className="tr-chart-empty">No numeric data for the selected filters</div>
            ) : (
              <svg id="tr-chart-svg" className="tr-chart-svg" viewBox={`0 0 ${c.W} ${c.H}`} preserveAspectRatio="none">
                {/* Per-series vertical gradient (full colour at the top → softer
                    at the base) so bars/areas read with depth instead of flat
                    blocks (user request 2026-09-24 — "make the bar chart
                    impressive"). */}
                <defs>
                  {c.built.map((s, si) => (
                    <linearGradient key={`g${si}`} id={`tr-bar-${si}`} x1="0" y1="0" x2="0" y2="1">
                      {/* Near-solid fill (reference grouped-column look, user request
                          2026-09-24) — just a hint of gradient for depth. */}
                      <stop offset="0%" stopColor={s.color} stopOpacity={1} />
                      <stop offset="100%" stopColor={s.color} stopOpacity={0.85} />
                    </linearGradient>
                  ))}
                </defs>
                {c.yTicks.map((t, i) => (
                  <g key={`y${i}`}>
                    <line x1={c.padL} y1={t.y} x2={c.W - c.padR} y2={t.y} stroke="var(--ai-line)" strokeWidth={1} />
                    <text x={c.padL - 8} y={t.y + 3} textAnchor="end" fontSize={10} fill="var(--ai-muted)">{t.label}</text>
                  </g>
                ))}
                {/* Y-axis TITLE — what the axis shows: the measured Result (+ unit
                    when every series shares one). Bold, inside the plot (user
                    request 2026-09-24). */}
                <text x={17} y={c.padT + c.plotH / 2} fontSize={12} fontWeight={400} fill="var(--ai-ink)"
                  transform={`rotate(-90 17 ${c.padT + c.plotH / 2})`} textAnchor="middle">
                  {units ? `Result (${units})` : 'Result'}
                </text>
                <line x1={c.padL} y1={c.padT + c.plotH} x2={c.W - c.padR} y2={c.padT + c.plotH} stroke="var(--ai-line)" strokeWidth={1} />
                {c.xTicks.map((t, i) => (
                  <g key={`x${i}`}>
                    <line x1={t.x} y1={c.padT + c.plotH} x2={t.x} y2={c.padT + c.plotH + 4} stroke="var(--ai-line)" strokeWidth={1} />
                    <text x={t.x} y={c.padT + c.plotH + 16} textAnchor="middle" fontSize={10} fill="var(--ai-muted)">{t.label}</text>
                  </g>
                ))}
                {/* X-axis TITLE — which column the axis plots: Date Analyzed.
                    Normal weight (user request 2026-09-24), kept clear of the
                    card's bottom edge. */}
                <text x={c.padL + (c.W - c.padL - c.padR) / 2} y={c.H - 16} fontSize={12} fontWeight={400}
                  fill="var(--ai-ink)" textAnchor="middle">Date Analyzed</text>
                {/* One area/line/bar set per parameter series, each its own colour. */}
                {c.built.map((s, si) => (
                  <g key={s.name}>
                    {chartType === 'area' && s.areaPath && (
                      <path d={s.areaPath} fill={s.color} fillOpacity={0.16} stroke="none" />
                    )}
                    {chartType !== 'bar' && (
                      <path d={s.linePath} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                    )}
                    {chartType === 'bar' && s.pts.map((p, i) => {
                      // Grouped bar: shift each series within the slot so bars sit
                      // side by side; TOP-only rounded corners + per-series gradient
                      // for a clean, modern bar look (user request 2026-09-24).
                      const w = c.barW - (c.barW > 6 ? 1.5 : 0.5);
                      const rawX = s.xs[i]! - c.groupW / 2 + si * c.barW + (c.barW - w) / 2;
                      // Never let a bar spill past the axis (user report 2026-09-24).
                      const x = Math.max(c.xMinPx, Math.min(rawX, c.xMaxPx - w));
                      const yTop = s.ys[i]!;
                      const h = Math.max(1, c.baseY - yTop);
                      const r = Math.min(c.barRx, w / 2, h);
                      // Path: up the left, rounded top-left → top-right, down the right,
                      // flat base — so only the TOP corners are rounded.
                      const d = `M ${x},${c.baseY} L ${x},${yTop + r} Q ${x},${yTop} ${x + r},${yTop} `
                        + `L ${x + w - r},${yTop} Q ${x + w},${yTop} ${x + w},${yTop + r} `
                        + `L ${x + w},${c.baseY} Z`;
                      return (
                        <path key={`b${i}`} className="tr-chart-bar" d={d} fill={`url(#tr-bar-${si})`}
                          stroke={s.color} strokeWidth={0.6} strokeOpacity={0.5}
                          onMouseEnter={(e) => showTip(e, p)} onMouseMove={moveTip} onMouseLeave={hideTip} />
                      );
                    })}
                    {/* Point markers on line/area only (never on bars). With MANY
                        series (e.g. 82 parameters) the dots overlap into clutter,
                        so they're hidden past 8 series — the lines alone read
                        cleaner (user request 2026-09-24). A small filled dot with a
                        white halo looks more polished than a hollow ring. */}
                    {chartType !== 'bar' && series.length <= 8 && s.pts.map((p, i) => (
                      <circle key={`d${i}`} className="tr-chart-dot" cx={s.xs[i]} cy={s.ys[i]} r={3.2}
                        fill={s.color} stroke="#fff" strokeWidth={1.4}
                        onMouseEnter={(e) => showTip(e, p)} onMouseMove={moveTip} onMouseLeave={hideTip} />
                    ))}
                    {/* Many-series mode: invisible wide hit-targets so hover still
                        works on the dot-less lines. */}
                    {chartType !== 'bar' && series.length > 8 && s.pts.map((p, i) => (
                      <circle key={`h${i}`} cx={s.xs[i]} cy={s.ys[i]} r={5} fill="transparent"
                        onMouseEnter={(e) => showTip(e, p)} onMouseMove={moveTip} onMouseLeave={hideTip} />
                    ))}
                  </g>
                ))}
              </svg>
            )}
          </div>
        </div>

        {/* Results search — shared .list-search-row ON the grey page (BO UI
            Standard v2), above its card, never inside it. */}
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input placeholder="Search job, sample, site, project…" value={gSearch}
              onChange={(e) => setGSearch(e.target.value)} />
          </div>
        </div>

        {/* Results grid — card holds the caption strip + grid only. */}
        <div className="tp-card">
          <div className="tp-card-hdr">
            {/* Card caption only — the ONE page heading (FormTitle + load-time
                chip) lives in the page header above (§18.6). */}
            <div className="tp-card-title">
              <i className="ti ti-table" /> Results
              <span className="count">({gridRows.length.toLocaleString()} rows)</span>
            </div>
          </div>
          {/* Shared data grid — styling, per-column filters, sort, footer /
              pagination and page-size are all standardised in <AppDataGrid>. */}
          <AppDataGrid<Row>
            rows={committed == null ? [] : gridRows}
            columns={gridCols}
            getRowId={(_r, i) => i}
            loading={committed != null && dataQ.isFetching}
            emptyText={committed == null ? 'No data — run a query above' : 'No results for the selected filters'}
          />
        </div>
      </div>
      <div className="tr-tip" ref={tipRef} />
    </div>
  );
}
